/** Pure helpers for Motion Reconstruction click-to-play / no-autoplay (V3).
 *
 * Kept free of React so node tests can lock the product contract without a
 * DOM. The React component (`MotionSkeletonPreview`) is the sole runtime
 * consumer of these helpers for mode-switch timeline mapping.
 */

import { findNearestMotionFrameByTime } from './motionVideoSyncCore'

/** Canonical product default — completed nodes never auto-start. */
export const AUTO_PLAY_ON_MOUNT = false as const

/** Canvas media end behavior (Interaction Contract V1 §4) — natural end stops,
 * returns to first frame; no infinite loop unless a node opts in explicitly. */
export const CANVAS_MEDIA_DEFAULT_LOOP = false as const

/** Returning to viewport / tab must not resume — user clicks again. */
export const OFFSCREEN_RETURN_AUTO_RESUME = false as const

export type MotionPlaybackState = 'paused' | 'playing'

/** Click toggle: playing ↔ paused. Single-frame content stays paused. */
export function toggleMotionPlaybackState(
  current: MotionPlaybackState,
  options?: { hasMultiFrame?: boolean },
): MotionPlaybackState {
  if (options?.hasMultiFrame === false) return 'paused'
  return current === 'playing' ? 'paused' : 'playing'
}

/**
 * Map video.currentTime → nearest 3D/skeleton frame index when leaving
 * Video+Skeleton for Skeleton-only (timeline preserved).
 */
export function skeletonFrameIndexForVideoTime(
  frames: Array<{ timestamp_ms?: number | null }>,
  videoCurrentTimeSeconds: number,
): number {
  return findNearestMotionFrameByTime(frames, videoCurrentTimeSeconds)
}

/**
 * Map skeleton frame index → video seconds when returning to Video+Skeleton.
 * Prefers frame.timestamp_ms; falls back to index/fps.
 */
export function videoTimeForSkeletonFrame(
  frames: Array<{ timestamp_ms?: number | null }>,
  frameIndex: number,
  fps = 12,
): number {
  const frame = frames[Math.max(0, Math.min(frameIndex, Math.max(frames.length - 1, 0)))]
  const tMs = frame?.timestamp_ms
  if (tMs != null && Number.isFinite(Number(tMs))) return Math.max(0, Number(tMs) / 1000)
  if (frames.length > 1) return frameIndex / Math.max(fps, 1)
  return 0
}
